// The clock-on QR card (CONTROL V4 "Clock-on QR", handoff §7e): what the
// dialog shows and what its PDF prints.
//
// The square is a Dataphyll Crew QR v2 (docs/crew-qr-v2.md), built by the API
// (GET /api/teams/:id/clock-on-qr) so the printed card and anything else that
// hands out a team's QR share one encoder. It carries the team's access code
// and the moment that code is replaced: the card stops clocking anyone on then
// (§7c), so the dialog and the PDF both say when.

export const QR_SCAN_NOTE =
  'Scan with the Dataphyll Crew app to clock on, or type the access code. A phone camera opens the app store page for Dataphyll Crew.'

// An induction card says what it is not: scanning it sets a new worker up
// with the client and their default team, and starts no shift.
export const QR_INDUCTION_NOTE =
  'Scan with the Dataphyll Crew app to be inducted. This is not a clock-on: it records no time and no pay, and there is nothing to clock off. To start work, scan your team’s Clock On QR. A phone camera opens the app store page for Dataphyll Crew.'

export interface QrRow {
  label: string
  value: string
}

export interface QrCard {
  kind: string
  teamName: string
  // What to do with it, for whoever finds the card on a post.
  note: string
  // The QR, row by row: '1' is a dark module. Empty until the API answers.
  modules: string[]
  rows: QrRow[]
  // The typed alternative to scanning, when the team has a fixed code.
  accessCode: string | null
  // The header colour, as the Crew app colours the mode: terracotta for
  // harvest (the default), sage for a receipting team.
  accent?: 'harvest' | 'receipt'
}

// ---- PDF ---------------------------------------------------------------
// A one-page A4 PDF written by hand: the card is a few rectangles and lines
// of Helvetica, which needs no library. Text goes out in WinAnsi with octal
// escapes, so the file itself is plain ASCII and its byte offsets are its
// character offsets.

// Te reo vowels with macrons are not in WinAnsi: the fonts' encoding maps them
// onto spare codes 1-10 by glyph name, so a team or site name like Ōtaki
// prints as written rather than as a question mark.
const MACRONS = ['Ā', 'ā', 'Ē', 'ē', 'Ī', 'ī', 'Ō', 'ō', 'Ū', 'ū']
export const ENCODING =
  '<< /Type /Encoding /BaseEncoding /WinAnsiEncoding /Differences [1 /Amacron /amacron /Emacron /emacron /Imacron /imacron /Omacron /omacron /Umacron /umacron] >>'

const WIN_ANSI: Record<string, number> = {
  '€': 0x80, '‚': 0x82, '„': 0x84, '…': 0x85, '‘': 0x91, '’': 0x92, '“': 0x93, '”': 0x94,
  '•': 0x95, '–': 0x96, '—': 0x97, '™': 0x99,
}

export function pdfText(s: string): string {
  let out = ''
  for (const ch of s) {
    const c = ch.codePointAt(0)!
    const m = MACRONS.indexOf(ch)
    const code = m >= 0 ? m + 1 : (WIN_ANSI[ch] ?? (c < 256 ? c : 0x3f))
    if (ch === '(' || ch === ')' || ch === '\\') out += '\\' + ch
    else if (code < 32 || code > 126) out += '\\' + code.toString(8).padStart(3, '0')
    else out += String.fromCharCode(code)
  }
  return `(${out})`
}

// Greedy wrap by an average Helvetica width (about half the font size).
export function wrap(text: string, size: number, width: number): string[] {
  const max = Math.max(10, Math.floor(width / (size * 0.5)))
  const lines: string[] = []
  let line = ''
  for (const word of text.split(/\s+/)) {
    if (line && (line + ' ' + word).length > max) {
      lines.push(line)
      line = word
    } else line = line ? line + ' ' + word : word
  }
  if (line) lines.push(line)
  return lines
}

export const rgb = (hex: string) =>
  [1, 3, 5].map((i) => (parseInt(hex.slice(i, i + 2), 16) / 255).toFixed(3)).join(' ')

const W = 595
const H = 842

// The card's page as drawing ops, for pdfDocument. The team schedule puts it
// in front of its job cards (schedule.ts).
export function qrCardPage(card: QrCard, printedOn: string): string {
  const cardW = 320
  const left = (W - cardW) / 2
  const top = H - 60
  const ops: string[] = []
  const text = (s: string, x: number, y: number, size: number, bold = false, colour = '#201e1d') =>
    ops.push(`BT /${bold ? 'F2' : 'F1'} ${size} Tf ${rgb(colour)} rg ${x.toFixed(1)} ${y.toFixed(1)} Td ${pdfText(s)} Tj ET`)
  const rect = (x: number, y: number, w: number, h: number, colour: string) =>
    ops.push(`${rgb(colour)} rg ${x.toFixed(1)} ${y.toFixed(1)} ${w.toFixed(1)} ${h.toFixed(1)} re f`)

  // Header band, in the accent the dialog uses.
  const headH = 64
  const receipt = card.accent === 'receipt'
  rect(left, top - headH, cardW, headH, receipt ? '#7a8a5e' : '#c67139')
  text(card.kind.toUpperCase(), left + 20, top - 24, 8, true, receipt ? '#e6ecdc' : '#fbe6d8')
  text(card.teamName, left + 20, top - 46, 17, true, '#ffffff')

  // The square, centred, about 7.5 cm across whatever its version: modules of
  // a millimetre or more scan from arm's length off a laminated card. Each
  // run of dark modules in a row is one rectangle.
  const qrSide = 212
  const n = card.modules.length
  const m = n ? qrSide / n : 0
  const qx = left + (cardW - qrSide) / 2
  const qyTop = top - headH - 24
  card.modules.forEach((row, r) => {
    for (let c = 0; c < n; ) {
      if (row[c] !== '1') {
        c++
        continue
      }
      let end = c
      while (end < n && row[end] === '1') end++
      ops.push(`0.125 0.118 0.114 rg ${(qx + c * m).toFixed(2)} ${(qyTop - (r + 1) * m).toFixed(2)} ${((end - c) * m).toFixed(2)} ${m.toFixed(2)} re f`)
      c = end
    }
  })
  let y = qyTop - qrSide - 22
  if (card.accessCode) {
    const label = `Access code ${card.accessCode}`
    text(label, left + (cardW - label.length * 6.4) / 2, y, 12, true)
    y -= 22
  }

  // What to do with it, for whoever finds the card on a post.
  const noteLines = wrap(card.note, 8.5, cardW - 40)
  const noteH = noteLines.length * 11.5 + 16
  rect(left + 12, y - noteH + 10, cardW - 24, noteH, '#eef1e8')
  noteLines.forEach((l, i) => text(l, left + 22, y - 4 - i * 11.5, 8.5, false, '#4b5a3a'))
  y -= noteH + 10

  // The detail table.
  for (const r of card.rows) {
    ops.push(`0.925 0.898 0.847 rg ${left + 20} ${(y - 8).toFixed(1)} ${cardW - 40} 0.6 re f`)
    text(r.label, left + 20, y, 9, true, '#857c6d')
    text(r.value, left + 130, y, 9.5, false)
    y -= 22
  }
  text(`Printed ${printedOn} from Dataphyll CONTROL`, left + 20, y - 6, 7.5, false, '#a89e8c')

  // Card outline.
  const bottom = y - 22
  ops.push(`0.886 0.855 0.796 RG 0.8 w ${left} ${bottom.toFixed(1)} ${cardW} ${(top - bottom).toFixed(1)} re S`)

  return ops.join('\n')
}

// A PDF of A4 pages, each given as its drawing ops, with Helvetica as F1 and
// Helvetica-Bold as F2. Objects: 1 catalog, 2 pages, 3-4 fonts, then a page
// and its contents per page.
export function pdfDocument(pages: string[]): string {
  const pageIds = pages.map((_, i) => 5 + i * 2)
  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pages.length} >>`,
    `<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding ${ENCODING} >>`,
    `<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding ${ENCODING} >>`,
  ]
  pages.forEach((stream, i) => {
    objects.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${W} ${H}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${pageIds[i] + 1} 0 R >>`,
      `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`
    )
  })
  let pdf = '%PDF-1.4\n'
  const offsets: number[] = []
  objects.forEach((body, i) => {
    offsets.push(pdf.length)
    pdf += `${i + 1} 0 obj\n${body}\nendobj\n`
  })
  const xref = pdf.length
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`
  for (const off of offsets) pdf += `${String(off).padStart(10, '0')} 00000 n \n`
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`
  return pdf
}

export const qrCardPdf = (card: QrCard, printedOn: string): string => pdfDocument([qrCardPage(card, printedOn)])

export const qrPdfName = (teamName: string) =>
  `clock-on-qr-${teamName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'team'}.pdf`
